import { IMPRESSION_CITATION_SYSTEM } from './ImpressionItem';

export interface ImpressionCitationSource {
  readonly investigation: fhir.ClinicalImpression['investigation'];
  readonly proposedCitations: fhir.Reference[] | undefined;
}

function isCitationBucket(inv: fhir.ClinicalImpressionInvestigation): boolean {
  return (
    inv.code?.text === IMPRESSION_CITATION_SYSTEM ||
    (inv.code?.coding?.some(c => c.system === IMPRESSION_CITATION_SYSTEM) ??
      false)
  );
}

/**
 * Rebuilds `investigation[]` when a diff is accepted.
 *
 * Snapshot path (proposedCitations != undefined):
 *   Merges the agent-staged list with the pre-diff citation bucket, filtered
 *   to entries whose identifier.value appears in the resolved summary. The
 *   merge is necessary because partial hunk rejection can leave old citation
 *   markers in the text that are absent from proposedCitations (which only
 *   reflects the agent's fully-accepted view). Dedup is by identifier.value
 *   so an ID kept by both sides isn't doubled. Non-citation investigations
 *   are untouched.
 *
 * Fallback path (proposedCitations == undefined):
 *   Preserves legacy behaviour — keeps existing items whose identifier.value
 *   is a substring of updatedSummary. Used for diffs written before the
 *   snapshot extension existed.
 *
 * The input investigation is never mutated; a new array is always returned.
 */
export function reconcileInvestigationOnApply(
  source: ImpressionCitationSource,
  updatedSummary: string,
): fhir.ClinicalImpression['investigation'] {
  const { investigation, proposedCitations } = source;

  if (!investigation?.length) {
    if (proposedCitations?.length) {
      // Agent added brand-new citations but the impression had no bucket yet.
      const filteredItems = proposedCitations.filter(
        ref =>
          ref.identifier?.value &&
          updatedSummary.includes(ref.identifier.value),
      );
      if (filteredItems.length === 0) return undefined;
      return [
        { code: { text: IMPRESSION_CITATION_SYSTEM }, item: filteredItems },
      ];
    }
    return investigation;
  }

  if (proposedCitations !== undefined) {
    // Snapshot path: merge agent-proposed citations with pre-diff existing
    // citations, then filter to only those referenced in the resolved summary.
    //
    // Merging (rather than replacing) is required for partial-rejection
    // correctness: if a hunk removes [^o0] and adds [^dr1] but the user
    // rejects it, [^o0] stays in the text but is absent from proposedCitations.
    // We recover it by pulling it from the pre-diff bucket.
    // Dedup by identifier.value so an ID present in both sides appears once.
    const existingItems = investigation.flatMap(inv =>
      isCitationBucket(inv) ? inv.item ?? [] : [],
    );
    const seenIds = new Set<string>();
    const mergedItems: fhir.Reference[] = [];
    for (const ref of [...proposedCitations, ...existingItems]) {
      const id = ref.identifier?.value;
      if (!id || seenIds.has(id)) continue;
      seenIds.add(id);
      mergedItems.push(ref);
    }
    const filteredItems = mergedItems.filter(
      ref =>
        ref.identifier?.value && updatedSummary.includes(ref.identifier.value),
    );
    const hasCitationBucket = investigation.some(isCitationBucket);
    const mapped = investigation
      .map(inv => {
        if (!isCitationBucket(inv)) return inv;
        if (filteredItems.length === 0) return null;
        return { ...inv, item: filteredItems };
      })
      .filter(
        (inv): inv is fhir.ClinicalImpressionInvestigation => inv !== null,
      );
    if (!hasCitationBucket && filteredItems.length > 0) {
      mapped.push({
        code: { text: IMPRESSION_CITATION_SYSTEM },
        item: filteredItems,
      });
    }
    return mapped;
  }

  // Fallback path: substring prune on existing items.
  return investigation.map(inv => {
    if (!isCitationBucket(inv) || !inv.item) return inv;
    return {
      ...inv,
      item: inv.item.filter(
        i => i.identifier?.value && updatedSummary.includes(i.identifier.value),
      ),
    };
  });
}
